import { useCallback, useEffect, useRef, useState } from "react";
import { notifyBugsnagError, shouldTriggerSearch } from "../common/utils/Functions";
import useFetch from "./useFetch";

export interface SelectOption {
  value: any;
  label: any;
}

interface UsePaginatedOrgOptionsArgs {
  /** API endpoint, defaults to the organization list. */
  url?: string;
  /** Gate the initial load (e.g. only for super-admins). */
  enabled?: boolean;
  /** Extra fields merged into every request body. */
  extraBody?: Record<string, any>;
  /** Map a raw API item to a { value, label } option. */
  mapItem?: (item: any) => SelectOption;
  /** Prepend an "All" option (value: null). Defaults to true. */
  includeAllOption?: boolean;
}

const SEARCH_DEBOUNCE_MS = 400;
const ALL_OPTION: SelectOption = { label: "All", value: null };

/**
 * Loads dropdown options in a single call by sending `is_drop_down: true` with
 * `limit: -1` (no pagination). Typing triggers a debounced server-side search
 * that re-fetches the full list. The `loadMore`/`loading` shape is preserved so
 * ReactSelectField keeps working, but infinite scroll is now a no-op.
 */
const usePaginatedOrgOptions = ({
  url = "/organization/list",
  enabled = true,
  extraBody = { search: "", stripe_status: null, is_active: 1 },
  mapItem = (item: any) => ({ value: item?.uuid, label: item?.name }),
  includeAllOption = true,
}: UsePaginatedOrgOptionsArgs = {}) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  const searchRef = useRef("");
  const inFlightRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const [listApi] = useFetch(url, { method: "post" });

  const fetchList = useCallback(
    async () => {
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      setLoading(true);
      try {
        const res = await listApi({
          ...extraBody,
          is_drop_down: true,
          start: 0,
          limit: -1,
          search: searchRef.current,
        });
        const list: any[] = res?.data?.list ?? [];
        const mapped = list.map(mapItem);

        setOptions(includeAllOption ? [ALL_OPTION, ...mapped] : mapped);
      } catch (e: any) {
        notifyBugsnagError(e, { api: "usePaginatedOrgOptions", url });
      } finally {
        inFlightRef.current = false;
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listApi, includeAllOption]
  );

  /** Initial load (re-runs when `enabled` flips on). */
  useEffect(() => {
    if (!enabled) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  /** Kept for API compatibility; the full list is already loaded. */
  const loadMore = useCallback(() => {}, []);

  /** Debounced server-side search wired to react-select's onInputChange. */
  const handleInputChange = useCallback(
    (input: string, actionMeta?: { action?: string }) => {
      if (actionMeta && actionMeta.action !== "input-change") return;
      searchRef.current = input ?? "";
      clearTimeout(debounceRef.current);
      // Empty input resets to the full list; 1-2 chars hold the current options.
      if (!shouldTriggerSearch(searchRef.current)) return;
      debounceRef.current = setTimeout(() => {
        fetchList();
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchList]
  );

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return { options, loading, loadMore, handleInputChange };
};

export default usePaginatedOrgOptions;
